import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  CircularProgress,
  FormControl,
  FormLabel,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import SaveIcon from '@material-ui/icons/Save';
import { useDataProvider, useNotify } from 'react-admin';
import {
  DAYS,
  deserializePlanning,
  minutesBetween,
  serializePlanning,
  timeToMinutes,
  toHumanDuration,
} from '../../CleanerContract/detail/Form/Metadata/planningUtils';

const generateId = () => {
  return `${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;
};

const ensureIds = (planningObj) => {
  const withIds = {};
  Object.keys(planningObj || {}).forEach((dayKey) => {
    const arr = Array.isArray(planningObj[dayKey]) ? planningObj[dayKey] : [];
    withIds[dayKey] = arr.map((s) => {
      return {
        id: s.id || generateId(),
        start: s.start,
        end: s.end,
      };
    });
  });
  return withIds;
};

const emptyPlanning = () => {
  return DAYS.reduce((acc, d) => {
    acc[d.key] = [];
    return acc;
  }, {});
};

const normalizePlanning = (obj) => {
  const base = emptyPlanning();
  const data = deserializePlanning(obj);
  return { ...base, ...data };
};

const validateShift = (shift) => {
  if (!shift.start || !shift.end) {
    return false;
  }
  return shift.start < shift.end;
};

// Merges shifts that overlap or touch (previous end === next start) on a single
// day into a single continuous shift. Shifts that are not valid yet (missing
// start/end, or end <= start) are left untouched and appended as-is: they are
// never silently merged away, so a genuinely broken range stays visible and
// blocks save (see validate()) instead of disappearing into a merge.
// Called only: (1) when a preset is clicked (attach onto the existing block),
// (2) by "copier vers tous les jours", and (3) once at save time on every day
// — never while the user is actively editing a field (see handleChangeShift).
const mergeShiftsForDay = (shifts) => {
  if (!Array.isArray(shifts)) {
    return [];
  }
  const valid = shifts.filter(validateShift);
  const invalid = shifts.filter((s) => {
    return !validateShift(s);
  });
  if (valid.length === 0) {
    return [...invalid];
  }
  const sorted = [...valid].sort((a, b) => {
    return timeToMinutes(a.start) - timeToMinutes(b.start);
  });
  const merged = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i += 1) {
    const last = merged[merged.length - 1];
    const current = sorted[i];
    if (timeToMinutes(current.start) <= timeToMinutes(last.end)) {
      if (timeToMinutes(current.end) > timeToMinutes(last.end)) {
        last.end = current.end;
      }
    } else {
      merged.push({ ...current });
    }
  }
  return [...merged, ...invalid];
};

// Quick presets (80/20): one click inserts a typical shift, then it is merged
// with whatever else is already on that day.
const PRESETS = [
  { label: 'Matin', start: '06:00', end: '09:00' },
  { label: 'Après-midi', start: '14:00', end: '18:00' },
  { label: 'Journée', start: '08:00', end: '17:00' },
];

const CUSTOM_SHIFT_DEFAULT = { start: '09:00', end: '12:00' };

const TimeSlotRow = ({ dayKey, index, shift, onChange, onDelete }) => {
  const valid = validateShift(shift);
  return (
    <Paper
      variant="outlined"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '4px 8px',
        margin: '4px 0',
        borderColor: valid ? '#d6dde6' : '#f44336',
        background: valid ? '#f9fafb' : '#fdecea',
      }}
    >
      <TextField
        type="time"
        label="Début"
        size="small"
        value={shift.start || ''}
        InputLabelProps={{ shrink: true }}
        inputProps={{ step: 300 }}
        onChange={(e) => {
          onChange(dayKey, index, { ...shift, start: e.target.value });
        }}
      />
      <Typography variant="body2" color="textSecondary">
        →
      </Typography>
      <TextField
        type="time"
        label="Fin"
        size="small"
        value={shift.end || ''}
        InputLabelProps={{ shrink: true }}
        inputProps={{ step: 300 }}
        onChange={(e) => {
          onChange(dayKey, index, { ...shift, end: e.target.value });
        }}
      />
      <div
        style={{
          flex: 1,
          textAlign: 'right',
          fontSize: 12,
          color: valid ? '#506174' : '#c62828',
        }}
      >
        {valid
          ? toHumanDuration(minutesBetween(shift.start, shift.end))
          : 'Horaire invalide'}
      </div>
      <IconButton
        aria-label="delete"
        size="small"
        onClick={() => {
          onDelete(dayKey, index);
        }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Paper>
  );
};

TimeSlotRow.propTypes = {
  dayKey: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  shift: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

// Availability planning (plan 014): disponibility now lives on the PERSON, not
// the cleaner fiche. It is stored in `care.cleaner_profile.availability`
// (keyed on the silae matricule, see ra-data-postgrest PRIMARY_KEYS), same
// serialized JSON-string format as before. Reads/writes go straight through
// the data provider — independent from the cleaner SimpleForm submit — mirroring
// the Vivier ProfileCard pattern.
const AvailabilityForm = ({ silaeId, sourceCleanerId, onSaved }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [planning, setPlanning] = useState(emptyPlanning);
  const [profile, setProfile] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!silaeId) {
      setProfile(null);
      setPlanning(emptyPlanning());
      setLoaded(true);
      return undefined;
    }
    let cancelled = false;
    setLoaded(false);
    dataProvider
      .getList('cleaner_profile', {
        sort: { field: 'silae_id', order: 'ASC' },
        pagination: { page: 1, perPage: 1 },
        filter: { silae_id: silaeId },
      })
      .then(({ data }) => {
        if (cancelled) return;
        if (data && data.length > 0) {
          setProfile(data[0]);
          setPlanning(ensureIds(normalizePlanning(data[0].availability || '')));
        } else {
          setProfile(null);
          setPlanning(emptyPlanning());
        }
      })
      .catch(() => {
        if (cancelled) return;
        setProfile(null);
        setPlanning(emptyPlanning());
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [silaeId, dataProvider]);

  // mergeOnAdd: the quick presets (Matin/Après-midi/Journée) are meant to
  // attach onto whatever is already on the day, so they merge immediately.
  // The generic "Ajouter une tranche" button must always yield a new,
  // independent editable row — merging it in right away (against the fixed
  // CUSTOM_SHIFT_DEFAULT template) silently swallowed the second shift before
  // the user could customize it, making multi-shift days impossible. It no
  // longer merges on blur either (see handleSave): merging now happens only
  // once, at save time, on every day.
  const handleAddShift = (dayKey, template, mergeOnAdd = true) => {
    setPlanning((prev) => {
      const newShift = {
        id: generateId(),
        start: template.start,
        end: template.end,
      };
      const dayShifts = [...(prev[dayKey] || []), newShift];
      const nextDayShifts = mergeOnAdd
        ? mergeShiftsForDay(dayShifts)
        : dayShifts;
      return { ...prev, [dayKey]: nextDayShifts };
    });
  };

  const handleChangeShift = (dayKey, index, newShift) => {
    setPlanning((prev) => {
      const arr = [...(prev[dayKey] || [])];
      const prevShift = arr[index] || {};
      arr[index] = {
        ...prevShift,
        ...newShift,
        id: prevShift.id || newShift.id || generateId(),
      };
      return { ...prev, [dayKey]: arr };
    });
  };

  const handleDeleteShift = (dayKey, index) => {
    setPlanning((prev) => {
      const arr = [...(prev[dayKey] || [])];
      arr.splice(index, 1);
      return { ...prev, [dayKey]: arr };
    });
  };

  const handleCopyDayToAll = (dayKey) => {
    const sourceShifts = mergeShiftsForDay(planning[dayKey] || []);
    if (sourceShifts.length === 0) {
      notify('Aucune tranche horaire à copier pour ce jour', 'warning');
      return;
    }
    const dayLabel =
      (
        DAYS.find((d) => {
          return d.key === dayKey;
        }) || {}
      ).label || dayKey;
    const confirmed = window.confirm(
      `Copier les tranches horaires de ${dayLabel} vers les 7 jours de la semaine ?\nCela remplacera les tranches existantes des autres jours.`,
    );
    if (!confirmed) {
      return;
    }
    setPlanning((prev) => {
      const next = { ...prev };
      DAYS.forEach((d) => {
        const cloned = sourceShifts.map((s) => {
          return { id: generateId(), start: s.start, end: s.end };
        });
        next[d.key] = mergeShiftsForDay(cloned);
      });
      return next;
    });
    notify(`Copié vers les 7 jours de la semaine (depuis ${dayLabel})`, 'info');
  };

  // Overlapping/touching shifts are no longer blocked here: they are resolved
  // automatically by mergeShiftsForDay at save time (see handleSave). Only a
  // genuinely broken range (end <= start, both fields filled in) still blocks
  // save — merging can't fix that, and it must never be silently dropped.
  const validate = () => {
    const invalidDays = DAYS.filter((d) => {
      return (planning[d.key] || []).some((s) => {
        return s.start && s.end && !validateShift(s);
      });
    }).map((d) => {
      return d.label;
    });

    if (invalidDays.length > 0) {
      return `Horaire de fin avant l'horaire de début pour: ${invalidDays.join(
        ', ',
      )}`;
    }
    return undefined;
  };

  const validationError = validate();

  const handleSave = async () => {
    if (!silaeId) {
      notify(
        'Matricule (silae_id) manquant — disponibilité non enregistrable',
        'warning',
      );
      return;
    }
    if (validationError) {
      notify(validationError, 'warning');
      return;
    }
    setSaving(true);
    // Auto-merge (80/20), applied once here rather than while the user is
    // still typing: shifts that touch or overlap on the same day are folded
    // into a single continuous shift before persisting. Invalid ranges
    // (blocked above by validationError) are never reached here.
    const mergedPlanning = DAYS.reduce((acc, d) => {
      acc[d.key] = mergeShiftsForDay(planning[d.key] || []);
      return acc;
    }, {});
    const availability = serializePlanning(mergedPlanning);
    const availabilityConfirmedAt = new Date().toISOString();
    try {
      // Upsert on silae_id (CREATE is an idempotent merge for cleaner_profile,
      // see ra-data-postgrest): the backfill pre-created a row for every
      // matricule, so a plain INSERT would hit cleaner_profile_pkey. This works
      // whether the row exists or not. Merge with the loaded profile to avoid
      // wiping sibling fields when the row already exists — but drop the loaded
      // record's server-managed columns (serial `id` PK, timestamps) so they
      // never reach the upsert body (ON CONFLICT (silae_id) cannot resolve a
      // conflict on `id`). The data provider strips these too; this keeps the
      // intent explicit at the call site.
      const {
        id: _ignoredId,
        created_at: _ignoredCreatedAt,
        updated_at: _ignoredUpdatedAt,
        ...profileFields
      } = profile || {};
      await dataProvider.create('cleaner_profile', {
        data: {
          ...profileFields,
          silae_id: silaeId,
          availability,
          availability_confirmed_at: availabilityConfirmedAt,
          source_cleaner_id:
            (profile && profile.source_cleaner_id) || sourceCleanerId || null,
        },
      });
      setProfile((prev) => {
        return {
          ...(prev || { silae_id: silaeId }),
          availability,
          availability_confirmed_at: availabilityConfirmedAt,
        };
      });
      setPlanning(mergedPlanning);
      notify('Disponibilité enregistrée', 'info');
      if (onSaved) onSaved(availability);
    } catch (err) {
      notify(
        err.message || "Échec de l'enregistrement de la disponibilité",
        'warning',
      );
    } finally {
      setSaving(false);
    }
  };

  if (!silaeId) {
    return (
      <Typography variant="body2" color="textSecondary">
        Aucun matricule (silae_id) sur cette fiche — la disponibilité ne peut
        pas être rattachée à la personne.
      </Typography>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {validationError && (
        <div style={{ color: 'red', fontSize: 14, marginBottom: 8 }}>
          {validationError}
        </div>
      )}

      {DAYS.map((d) => {
        const shifts = planning[d.key] || [];
        return (
          <FormControl
            key={d.key}
            component="fieldset"
            fullWidth
            style={{
              border: '1px solid #eee',
              padding: 12,
              borderRadius: 6,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 8,
              }}
            >
              <FormLabel component="legend">{d.label}</FormLabel>
              <Button
                size="small"
                startIcon={<FileCopyIcon fontSize="small" />}
                disabled={shifts.length === 0}
                onClick={() => {
                  handleCopyDayToAll(d.key);
                }}
              >
                Copier vers tous les jours
              </Button>
            </div>

            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                marginTop: 8,
              }}
            >
              {PRESETS.map((preset) => {
                return (
                  <Button
                    key={preset.label}
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      handleAddShift(d.key, preset);
                    }}
                  >
                    {preset.label}
                  </Button>
                );
              })}
              <Button
                size="small"
                startIcon={<AddIcon fontSize="small" />}
                onClick={() => {
                  handleAddShift(d.key, CUSTOM_SHIFT_DEFAULT, false);
                }}
              >
                Ajouter une tranche
              </Button>
            </div>

            <div style={{ marginTop: 8 }}>
              {shifts.length === 0 ? (
                <div style={{ color: '#777', fontSize: 12 }}>
                  Aucune tranche horaire
                </div>
              ) : (
                shifts.map((s, idx) => {
                  return (
                    <TimeSlotRow
                      key={s.id || `${d.key}-${idx}`}
                      dayKey={d.key}
                      index={idx}
                      shift={s}
                      onChange={handleChangeShift}
                      onDelete={handleDeleteShift}
                    />
                  );
                })
              )}
            </div>
          </FormControl>
        );
      })}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSave}
          disabled={!loaded || saving || !!validationError}
          startIcon={
            saving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
        >
          Enregistrer la disponibilité
        </Button>
      </div>
    </div>
  );
};

AvailabilityForm.propTypes = {
  silaeId: PropTypes.string,
  sourceCleanerId: PropTypes.string,
  onSaved: PropTypes.func,
};

AvailabilityForm.defaultProps = {
  silaeId: '',
  sourceCleanerId: '',
  onSaved: undefined,
};

export default AvailabilityForm;
